import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getDefaultContent, getPageIcon } from "../pageTypes";
import { useApi } from "./ApiContext";
import { useProject } from "./ProjectContext";

const AppContext = createContext();

export const AppProvider = ({ children }) => {
	const [pages, setPages] = useState([]);
	const [activePage, setActivePage] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [pendingChanges, setPendingChanges] = useState([]);
	const { api, createPage: apiCreatePage, updatePage: apiUpdatePage, deletePage: apiDeletePage, getPages: apiGetPages, updateCanvas: apiUpdateCanvas } = useApi();
	const { currentProject } = useProject();

	// Load initial data
	useEffect(() => {
		const loadInitialData = async () => {
			if (!currentProject) {
				setIsLoading(false);
				return;
			}
			try {
				if (!currentProject.pages) {
					setPages([]);
					setIsLoading(false);
					return;
				}

				const pagesWithIcons = currentProject.pages.map((page) => ({
					...page,
					icon: getPageIcon(page.type),
				}));

				setPages(pagesWithIcons);

				if (pagesWithIcons.length > 0 && !activePage) {
					setActivePage(pagesWithIcons[0].id);
				}

				setIsLoading(false);
			} catch (err) {
				console.error("Error loading initial data:", err);
				setError(err.message || "Failed to load pages");
				setPages([]);
				setIsLoading(false);
			}
		};

		setIsLoading(true);
		loadInitialData();
	}, [currentProject, apiGetPages]);

	// Function to process pending changes
	const processPendingChanges = useCallback(async () => {
		if (pendingChanges.length === 0 || !currentProject) return;

		const change = pendingChanges[0];
		try {
			let response;
			switch (change.type) {
				case "create":
					try {
						response = await apiCreatePage(currentProject.id, change.pageType, change.title);

						if (!response || !response.id) {
							throw new Error("Invalid response from create page API");
						}

						setPages((prevPages) => {
							const updatedPages = prevPages.map((p) =>
								p.id === change.tempId
									? {
											...response,
											id: response.id,
											icon: getPageIcon(change.pageType),
									  }
									: p
							);
							return updatedPages;
						});
						setActivePage(response.id);
					} catch (createError) {
						console.error("Error creating page:", createError);
						setPages((prevPages) => prevPages.filter((p) => p.id !== change.tempId));
						setActivePage((current) => (current === change.tempId ? null : current));
						throw createError;
					}
					break;

				case "update":
					if (change.target === "canvas") {
						// For canvas updates, send both revisions and current state

						response = await apiUpdateCanvas(currentProject.id, {
							...change.content,
							currentState: pages,
							revisions: change.revisions || [],
						});

						if (change.onSuccess) {
							change.onSuccess(response);
						}
					} else {
						response = await apiUpdatePage(change.pageId, change.content);

						if (!response) {
							throw new Error("No response from update API");
						}

						setPages((prevPages) => {
							const updatedPages = prevPages.map((p) =>
								p.id === change.pageId
									? {
											...p,
											content: response.content,
											updatedAt: response.updatedAt,
											revisions: [
												{
													id: Date.now(),
													content: p.content,
													createdAt: new Date().toISOString(),
													revisionType: "update",
												},
												...(p.revisions || []),
											],
									  }
									: p
							);
							return updatedPages;
						});
					}
					break;

				case "delete":
					try {
						await apiDeletePage(change.pageId, currentProject.id);
						if (change.onSuccess) {
							change.onSuccess();
						}
					} catch (deleteError) {
						console.error("Error deleting page:", deleteError);
						if (change.onError) {
							change.onError();
						}
						throw deleteError;
					}
					break;

				default:
					console.warn("Unknown change type:", change.type);
					break;
			}

			setPendingChanges((prev) => prev.slice(1));
		} catch (error) {
			console.error("Error processing change:", error);
			setError(`Error processing change: ${error.message}`);
		}
	}, [pendingChanges, apiCreatePage, apiUpdatePage, apiDeletePage, apiUpdateCanvas, currentProject, pages]);

	// Process pending changes whenever they change
	useEffect(() => {
		if (isLoading) return; // Don't process changes while loading
		if (!currentProject) return; // Make sure we have project data

		processPendingChanges();
	}, [processPendingChanges, isLoading, currentProject]);

	const addPage = (pageType, customTitle) => {
		if (!currentProject) {
			const errorMsg = "Cannot create page: No current project found";
			console.error(errorMsg);
			setError(errorMsg);
			return;
		}

		if (!pageType) {
			const errorMsg = "Cannot create page: Page type is required";
			console.error(errorMsg);
			setError(errorMsg);
			return;
		}

		const title = customTitle || pageType.charAt(0).toUpperCase() + pageType.slice(1);
		console.log("Adding page:", { projectId: currentProject.id, pageType, title });

		const tempId = `temp-${Date.now()}`;
		const newPage = {
			id: tempId,
			type: pageType,
			title,
			path: `/${pageType.toLowerCase()}`,
			icon: getPageIcon(pageType),
			content: getDefaultContent(pageType),
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			revisions: [],
		};

		setPages((prevPages) => [...prevPages, newPage]);
		setActivePage(tempId);

		// Add to pending changes
		setPendingChanges((prev) => [
			...prev,
			{
				type: "create",
				projectId: currentProject.id,
				pageType,
				title,
				tempId,
			},
		]);

		return newPage;
	};

	const updatePage = (pageId, updates) => {
		setPages((prevPages) =>
			prevPages.map((page) =>
				page.id === pageId
					? {
							...page,
							...updates,
							updatedAt: new Date().toISOString(),
					  }
					: page
			)
		);

		// Add to pending changes
		setPendingChanges((prev) => [
			...prev,
			{
				type: "update",
				pageId,
				content: updates,
			},
		]);
	};

	const updatePageLayers = (pageId, sectionId, newLayers) => {
		setPages((prevPages) =>
			prevPages.map((page) => {
				if (page.id !== pageId) return page;

				const updatedSections = page.content.sections.map((section) => {
					if (section.id !== sectionId) return section;
					return { ...section, layers: newLayers };
				});

				const newContent = {
					...page.content,
					sections: updatedSections,
				};

				// Add to pending changes
				setPendingChanges((prev) => [
					...prev,
					{
						type: "update",
						pageId,
						content: newContent,
					},
				]);

				return {
					...page,
					content: newContent,
					updatedAt: new Date().toISOString(),
				};
			})
		);
	};

	const updatePageSections = (pageId, newSections) => {
		setPages((prevPages) =>
			prevPages.map((page) => {
				if (page.id !== pageId) return page;

				const newContent = {
					...page.content,
					sections: newSections,
				};

				// Add to pending changes
				setPendingChanges((prev) => [
					...prev,
					{
						type: "update",
						pageId,
						content: newContent,
					},
				]);

				return {
					...page,
					content: newContent,
					updatedAt: new Date().toISOString(),
				};
			})
		);
	};

	const toggleLayerVisibility = (pageId, sectionId, layerId) => {
		setPages((prevPages) =>
			prevPages.map((page) => {
				if (page.id !== pageId) return page;

				const updatedSections = page.content.sections.map((section) => {
					if (section.id !== sectionId) return section;

					const updatedLayers = section.layers.map((layer) => {
						if (layer.id !== layerId) return layer;
						return { ...layer, isVisible: !layer.isVisible };
					});

					return { ...section, layers: updatedLayers };
				});

				const newContent = {
					...page.content,
					sections: updatedSections,
				};

				// Add to pending changes
				setPendingChanges((prev) => [
					...prev,
					{
						type: "update",
						pageId,
						content: newContent,
					},
				]);

				return {
					...page,
					content: newContent,
					updatedAt: new Date().toISOString(),
				};
			})
		);
	};

	const deletePage = (pageId) => {
		if (!currentProject) {
			const errorMsg = "Cannot delete page: No current project found";
			console.error(errorMsg);
			setError(errorMsg);
			return;
		}

		const pageToDelete = pages.find((page) => page.id === pageId);
		if (!pageToDelete) {
			const errorMsg = `Cannot delete page: Page with ID ${pageId} not found`;
			console.error(errorMsg);
			setError(errorMsg);
			return;
		}

		console.log("Initiating page deletion:", { pageId });

		// Store the current active page and remaining pages before deletion
		const isActivePage = activePage === pageId;
		const remainingPages = pages.filter((p) => p.id !== pageId);
		const nextActivePage = isActivePage ? (remainingPages.length > 0 ? remainingPages[0].id : null) : activePage;

		// Add to pending changes
		setPendingChanges((prev) => [
			...prev,
			{
				type: "delete",
				pageId,
				projectId: currentProject.id,
				onSuccess: () => {
					// Update active page after successful deletion
					if (isActivePage) {
						setActivePage(nextActivePage);
					}
					// Update pages state
					setPages(remainingPages);
				},
				onError: () => {
					// If deletion fails, we don't change anything
					console.error("Failed to delete page, keeping current state");
				},
			},
		]);
	};

	// New function to handle reverting to a specific revision
	const revertToRevision = useCallback(
		(pageId, revisionId) => {
			setPages((prevPages) =>
				prevPages.map((page) => {
					if (page.id !== pageId) return page;

					const revision = page.revisions.find((r) => r.id === revisionId);
					if (!revision) return page;

					return {
						...page,
						content: revision.content,
						updatedAt: new Date().toISOString(),
						revisions: [
							{
								id: Date.now(),
								content: page.content,
								createdAt: new Date().toISOString(),
								revisionType: "revert",
							},
							...page.revisions,
						],
					};
				})
			);

			// Add to pending changes
			setPendingChanges((prev) => [
				...prev,
				{
					type: "update",
					pageId,
					content: pages.find((p) => p.id === pageId)?.revisions.find((r) => r.id === revisionId)?.content,
				},
			]);
		},
		[pages]
	);

	const updatePages = (newOrder) => {
		try {
			// Create a map of current pages for easy lookup
			const pageMap = new Map(pages.map((page) => [page.id, page]));

			// Create the reordered array using the new order
			const reorderedPages = newOrder.map((id) => pageMap.get(id));

			// Update the pages state immediately for UI responsiveness
			setPages(reorderedPages);

			// Add to pending changes with proper content structure for canvas update
			setPendingChanges((prev) => [
				...prev,
				{
					type: "update",
					target: "canvas",
					projectId: currentProject.id,
					content: {
						pages: reorderedPages.map((page) => ({
							id: page.id,
							type: page.type,
							title: page.title,
						})),
						action: "reorder",
					},
					onSuccess: (response) => {
						// Update revisions if provided in response
						if (response?.revisions) {
							setPages((prevPages) =>
								prevPages.map((page) => ({
									...page,
									revisions: [...(response.revisions[page.id] || []), ...(page.revisions || [])],
								}))
							);
						}
					},
				},
			]);
		} catch (error) {
			console.error("Error updating pages:", error);
			// Revert to previous state if there's an error
			setPages(pages);
			setError("Failed to update pages. Please try again.");
		}
	};

	const saveApp = useCallback(
		async (newState) => {
			if (!currentProject || !newState) {
				setError("No pages to save or no project selected");
				return Promise.reject(new Error("No pages to save or no project selected"));
			}
			console.log("saving app", newState);
			const response = await apiUpdateCanvas(currentProject.id, {
				...newState,
				action: "save",
			});
			if (!response) {
				throw new Error("No response from update canvas API");
			}
		},
		[currentProject]
	);

	return (
		<AppContext.Provider
			value={{
				// State
				pages,
				activePage,
				isLoading,
				error,
				pendingChanges,

				// Setters
				setActivePage,
				setPages,

				// Actions
				addPage,
				updatePage,
				updatePages,
				updatePageLayers,
				updatePageSections,
				toggleLayerVisibility,
				deletePage,
				revertToRevision,
				saveApp,
			}}>
			{children}
		</AppContext.Provider>
	);
};

export const useApp = () => {
	const context = useContext(AppContext);
	if (!context) {
		throw new Error("useApp must be used within an AppProvider");
	}
	return context;
};
